import { FormArray, FormControl, FormGroup } from '@angular/forms';

import { ReactiveFormsService } from '../../../shared/services/reactive-forms.service';
import { extractWorkflowRichTextHtml } from '../../utils/workflow-locale-text.util';

export function getFormStepOptionLabelControl(
	optionsArray: FormArray,
	optionIndex: number,
	localeId: string,
): FormControl {
	return optionsArray.at(optionIndex)?.get('label')?.get(localeId) as FormControl;
}

export function resolveFormStepOptionAnchorId(optionIndex: number): string {
	return `form-step-option-${optionIndex}`;
}

export function isFormStepOptionLabelEmpty(value: unknown, richText: boolean): boolean {
	if (richText) {
		return extractWorkflowRichTextHtml(value).length === 0;
	}

	return (value ?? '').toString().trim().length === 0;
}

/** Paridad con `form-step-instructions.localeFieldHasError` para labels de opción. */
export function formStepOptionLabelHasError(
	reactiveFormService: ReactiveFormsService,
	optionsArray: FormArray,
	optionIndex: number,
	localeId: string,
	richText: boolean,
): boolean {
	const localeControl = getFormStepOptionLabelControl(optionsArray, optionIndex, localeId);
	if (reactiveFormService.hasError(localeControl)) {
		return true;
	}

	const labelGroup = optionsArray.at(optionIndex)?.get('label') as FormGroup | null;
	if (!labelGroup || !reactiveFormService.hasError(labelGroup as unknown as Partial<FormControl>)) {
		return false;
	}

	return isFormStepOptionLabelEmpty(localeControl.value, richText);
}

export function formStepOptionLabelErrorMessage(
	reactiveFormService: ReactiveFormsService,
	optionsArray: FormArray,
	optionIndex: number,
	localeId: string,
	richText: boolean,
	requiredMessage: string,
): string {
	const localeControl = getFormStepOptionLabelControl(optionsArray, optionIndex, localeId);
	const controlErrorMessage = reactiveFormService.hasMsgError(localeControl);
	if (controlErrorMessage) {
		return controlErrorMessage;
	}

	if (formStepOptionLabelHasError(reactiveFormService, optionsArray, optionIndex, localeId, richText)) {
		return requiredMessage;
	}

	return '';
}

export function focusFormStepOptionField(optionIndex: number, richText: boolean): void {
	const anchorId = resolveFormStepOptionAnchorId(optionIndex);
	const focusSelector = richText ? '[contenteditable="true"], textarea, input' : 'input, textarea';

	setTimeout(() => {
		document.getElementById(anchorId)?.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
			inline: 'nearest',
		});
		const focusableInput = document
			.getElementById(anchorId)
			?.querySelector(focusSelector) as HTMLElement | null;
		focusableInput?.focus?.();
	}, 0);
}

export function focusFormStepOptionFieldIfInvalid(
	reactiveFormService: ReactiveFormsService,
	optionsArray: FormArray,
	optionIndex: number,
	localeId: string,
	richText: boolean,
): boolean {
	if (!formStepOptionLabelHasError(reactiveFormService, optionsArray, optionIndex, localeId, richText)) {
		return false;
	}

	focusFormStepOptionField(optionIndex, richText);
	return true;
}
